import { create } from "zustand";
import { api } from "../api/client";

/**
 * Notes drawn on the map.
 *
 * They live on the server, keyed to the replay, so a share link carries them
 * and a reload does not lose them. Points are kept in world centimetres, never
 * in pixels, so a note stays on the ground it was put on when the window is
 * resized. Each one belongs to a round: a note about a retake has nothing to
 * say three rounds earlier.
 *
 * Every change is applied locally first and sent afterwards. Drawing is a
 * dragging gesture and waiting on a round trip to see your own stroke would
 * make it feel broken. A write that fails is not patched up by guesswork: the
 * notes are fetched again, so what is on screen is what is stored.
 *
 * Only the owner of a replay changes them. Somebody who came in through its
 * share link sees every note and can change none: the store loads read-only
 * for them, and the server would refuse the writes anyway.
 *
 * Each round keeps its own undo history. Undoing an add deletes the row and
 * redoing it writes a new one, so identities change across an undo; nothing
 * outside this file holds on to one.
 */
export type Tool = "none" | "pen" | "line" | "arrow" | "text" | "erase";

export type Shape = "pen" | "line" | "arrow" | "text";

export interface Drawing {
  id: number;
  round: number;
  shape: Shape;
  color: string;
  /** World centimetres. A line and an arrow keep two, a text note one, and a
   *  pen stroke keeps all of them. */
  points: Array<[number, number]>;
  /** What a text note says; a note of any other shape has none. */
  text?: string;
}

/** The colours on the palette, chosen to stay legible over a grey minimap and
 *  apart from the two team colours. */
export const PEN_COLORS = ["#ffd166", "#ff6b6b", "#5ddc7a", "#7ad4ff", "#ffffff"];

/** One reversible change. Held in both directions: undo walks it one way and
 *  redo the other. */
type Action =
  | { kind: "add"; drawing: Drawing }
  | { kind: "move"; id: number; before: Points; after: Points }
  /** One or many gone at once: a sweep of the eraser, or clearing the round. */
  | { kind: "remove"; drawings: Drawing[] };

type Points = Array<[number, number]>;

interface History {
  past: Action[];
  future: Action[];
}

interface State {
  tool: Tool;
  color: string;
  /** Where the notes are read from: the owner's address or the share link. */
  base: string | null;
  /** The replay the notes are written to, or null when they are only to be
   *  looked at. Nothing is sent before one is loaded. */
  replayId: string | null;
  drawings: Drawing[];
  history: Map<number, History>;
  /** What the eraser has taken since the button went down, or null when it is
   *  not sweeping. */
  sweep: Drawing[] | null;
  /** Set when the last write failed, for the toolbar to show. */
  error: string | null;

  setTool: (tool: Tool) => void;
  setColor: (color: string) => void;
  load: (base: string, replayId: string | null) => Promise<void>;
  add: (round: number, shape: Shape, points: Points, text?: string) => Promise<void>;
  /** Opens an eraser sweep: everything it takes until it ends is one step. */
  beginErase: () => void;
  eraseOne: (id: number) => Promise<void>;
  endErase: (round: number) => Promise<void>;
  nudge: (id: number, dx: number, dy: number) => void;
  commitMove: (id: number, before: Points) => Promise<void>;
  clear: (round: number) => Promise<void>;
  undo: (round: number) => Promise<void>;
  redo: (round: number) => Promise<void>;
}

/** Identity for a note the server has not answered for yet. Negative, so it can
 *  never collide with one the server gave. */
let pendingID = -1;

export const useDrawings = create<State>((set, get) => {
  /**
   * Writes run one after another.
   *
   * A sweep of the eraser fires a deletion per thing it passes over, and they
   * would otherwise race: each reads what has been taken so far, and the slower
   * ones write back a list that no longer has the quicker ones in it. Queuing
   * them also lets the end of the sweep wait for the lot before recording the
   * step, so undo brings back everything the hand took rather than the last of
   * it.
   */
  let queue: Promise<unknown> = Promise.resolve();
  const serial = <T>(work: () => Promise<T>): Promise<T> => {
    const next = queue.then(work, work);
    queue = next.catch(() => undefined);
    return next;
  };

  /** Puts the local copy back in step with the server after a failed write. */
  const resync = async (message: string) => {
    set({ error: message });
    const { base } = get();
    if (!base) return;
    try {
      set({ drawings: await api.listDrawings(base), history: new Map() });
    } catch {
      // Already showing an error; nothing better to say.
    }
  };

  /** Records a change, which also means there is nothing to redo any more. */
  const record = (round: number, action: Action) =>
    set((s) => {
      const history = new Map(s.history);
      const entry = history.get(round) ?? { past: [], future: [] };
      history.set(round, { past: [...entry.past, action], future: [] });
      return { history };
    });

  /** Writes one note and puts it in the local copy under the id it was given. */
  const create = async (d: Omit<Drawing, "id">): Promise<Drawing | null> => {
    const { replayId } = get();
    if (!replayId) return null;
    const local: Drawing = { ...d, id: pendingID-- };
    set((s) => ({ drawings: [...s.drawings, local], error: null }));
    try {
      const saved = await api.addDrawing(replayId, d);
      set((s) => ({ drawings: s.drawings.map((x) => (x.id === local.id ? saved : x)) }));
      return saved;
    } catch {
      set((s) => ({ drawings: s.drawings.filter((x) => x.id !== local.id) }));
      await resync("le tracé n'a pas pu être enregistré");
      return null;
    }
  };

  /** Removes one note from the local copy and from the server. */
  const destroy = async (id: number): Promise<boolean> => {
    const { replayId, drawings } = get();
    const gone = drawings.find((d) => d.id === id);
    if (!replayId || !gone) return false;
    set({ drawings: drawings.filter((d) => d.id !== id), error: null });
    // A note the server never saw only ever existed here.
    if (id < 0) return true;
    try {
      await api.deleteDrawing(replayId, id);
      return true;
    } catch {
      await resync("le tracé n'a pas pu être supprimé");
      return false;
    }
  };

  /** Puts one note's points back where an action says they belong. */
  const place = async (id: number, points: Points): Promise<boolean> => {
    const { replayId } = get();
    if (!replayId) return false;
    set((s) => ({
      drawings: s.drawings.map((d) => (d.id === id ? { ...d, points } : d)),
      error: null,
    }));
    if (id < 0) return true;
    try {
      await api.moveDrawing(replayId, id, points);
      return true;
    } catch {
      await resync("le déplacement n'a pas pu être enregistré");
      return false;
    }
  };

  /** Walks one action backwards, and returns it as it now stands. */
  const revert = async (action: Action): Promise<Action | null> => {
    switch (action.kind) {
      case "add":
        return (await destroy(action.drawing.id)) ? action : null;
      case "move":
        return (await place(action.id, action.before)) ? action : null;
      case "remove": {
        const back: Drawing[] = [];
        for (const d of action.drawings) {
          const saved = await create(d);
          if (!saved) return null;
          back.push(saved);
        }
        return { kind: "remove", drawings: back };
      }
    }
  };

  /** Walks one action forwards again. */
  const replay = async (action: Action): Promise<Action | null> => {
    switch (action.kind) {
      case "add": {
        const saved = await create(action.drawing);
        return saved ? { kind: "add", drawing: saved } : null;
      }
      case "move":
        return (await place(action.id, action.after)) ? action : null;
      case "remove": {
        for (const d of action.drawings) {
          if (!(await destroy(d.id))) return null;
        }
        return action;
      }
    }
  };

  return {
    tool: "none",
    color: PEN_COLORS[0]!,
    base: null,
    replayId: null,
    drawings: [],
    history: new Map(),
    sweep: null,
    error: null,

    setTool: (tool) => set({ tool }),
    setColor: (color) => set({ color }),

    load: async (base, replayId) => {
      // Clear first: the notes on screen belong to the replay being left. A
      // tool picked on a replay that could be drawn on means nothing on one
      // that cannot.
      set({
        base,
        replayId,
        drawings: [],
        history: new Map(),
        sweep: null,
        error: null,
        ...(replayId ? {} : { tool: "none" as Tool }),
      });
      try {
        const drawings = await api.listDrawings(base);
        if (get().base === base) set({ drawings });
      } catch {
        set({ error: "les tracés n'ont pas pu être chargés" });
      }
    },

    add: async (round, shape, points, text) => {
      const saved = await create({ round, shape, points, text, color: get().color });
      if (saved) record(round, { kind: "add", drawing: saved });
    },

    // The eraser works by being dragged over things, so one pass of the hand
    // is one step however many it takes with it.
    beginErase: () => set({ sweep: [] }),

    eraseOne: (id) =>
      serial(async () => {
        const gone = get().drawings.find((d) => d.id === id);
        if (!gone || !(await destroy(id))) return;
        if (get().sweep) set((s) => ({ sweep: s.sweep ? [...s.sweep, gone] : s.sweep }));
        else record(gone.round, { kind: "remove", drawings: [gone] });
      }),

    endErase: async (round) => {
      // Everything the sweep started has to have finished before it is one step.
      await queue;
      const sweep = get().sweep;
      set({ sweep: null });
      if (sweep && sweep.length > 0) record(round, { kind: "remove", drawings: sweep });
    },

    // Dragging moves the note here and only here; the server hears about it
    // once the hand comes off, not sixty times a second.
    nudge: (id, dx, dy) =>
      set((s) => ({
        drawings: s.drawings.map((d) =>
          d.id === id
            ? { ...d, points: d.points.map(([x, y]) => [x + dx, y + dy] as [number, number]) }
            : d,
        ),
      })),

    commitMove: async (id, before) => {
      const moved = get().drawings.find((d) => d.id === id);
      if (!moved) return;
      const after = moved.points;
      if (await place(id, after)) {
        record(moved.round, { kind: "move", id, before, after });
      }
    },

    clear: async (round) => {
      const { replayId, drawings } = get();
      if (!replayId) return;
      const gone = drawings.filter((d) => d.round === round);
      if (gone.length === 0) return;
      set({ drawings: drawings.filter((d) => d.round !== round), error: null });
      try {
        await api.clearDrawings(replayId, round);
        record(round, { kind: "remove", drawings: gone });
      } catch {
        await resync("les tracés n'ont pas pu être supprimés");
      }
    },

    undo: async (round) => {
      const entry = get().history.get(round);
      const action = entry?.past[entry.past.length - 1];
      if (!entry || !action) return;
      const done = await revert(action);
      if (!done) return;
      set((s) => {
        const history = new Map(s.history);
        history.set(round, {
          past: entry.past.slice(0, -1),
          future: [...entry.future, done],
        });
        return { history };
      });
    },

    redo: async (round) => {
      const entry = get().history.get(round);
      const action = entry?.future[entry.future.length - 1];
      if (!entry || !action) return;
      const done = await replay(action);
      if (!done) return;
      set((s) => {
        const history = new Map(s.history);
        history.set(round, {
          past: [...entry.past, done],
          future: entry.future.slice(0, -1),
        });
        return { history };
      });
    },
  };
});

/** The drawings of one round, oldest first. */
export function drawingsOf(drawings: Drawing[], round: number): Drawing[] {
  return drawings.filter((d) => d.round === round);
}
